import styled, { css } from 'styled-components'
import theme from 'stories/utils/theme'
import { KnowSeoInfo } from 'interfaces/KnowSeoInfo'
import { Body1, Body2 } from 'stories/elements/Typography/Text'
import NextImage from 'components/elements/NextImage'
import { formatDate } from 'lib/formatting/datetime'
import { parseLocale } from 'lib/constants/localization'
import { useRouter } from 'next/router'
import { trackEvent } from 'lib/tracking/analytics-service'
import {
    AnalyticsEventKnowConversionLike,
    AnalyticsEventKnowConversionSave,
    AnalyticsEventKnowConversionShare,
} from 'lib/constants/AnalyticsEventType'
import ProfilePicture from 'components/elements/ProfilePicture'
import Stack from 'components/elements/Stack'
import LikeIcon from 'assets/ai-enhanced/app_style_like.svg'
import SaveIcon from 'assets/ai-enhanced/app_style_bookmark.svg'
import CommentIcon from 'assets/ai-enhanced/app_style_comment.svg'
import DownloadIcon from 'assets/ai-enhanced/app_style_download.svg'
import ViewIcon from 'assets/icons/webapp_views.svg'
import _useTranslation from 'lib/hooks/_useTranslation'

const Wrapper = styled.div<{ noPadding?: boolean }>`
    position: relative;
    display: grid;
    gap: 5px;
    min-height: calc(100vh - 50px);

    ${({ noPadding }) =>
        noPadding
            ? css`
                  min-height: calc(100vh - 100px);
              `
            : ''}
`

const Container = styled.div<{ noPadding?: boolean }>`
    position: absolute;
    bottom: 45px;
    left: -20px;
    width: 100vw;
    height: calc(100% + var(--navbar-height));
    background: ${theme.colors.white};
    display: flex;
    flex-direction: column;
    background: linear-gradient(180.22deg, rgba(9, 12, 15, 0.6) 0.19%, rgba(9, 12, 15, 0) 99.81%);
    overflow: auto;
    box-sizing: border-box;

    ${({ noPadding }) =>
        noPadding
            ? css`
                  left: 0;
                  top: 0px;
              `
            : ''}
`

const ImageContainer = styled.div`
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${theme.colors.white};
    overflow: hidden;
    cursor: pointer;

    &::before,
    &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        height: 120px;
        pointer-events: none;
        z-index: 1;
    }

    &::before {
        top: 0;
        background: linear-gradient(180.22deg, rgba(9, 12, 15, 0.6) 0.19%, rgba(9, 12, 15, 0) 99.81%);
        height: 100px;
    }

    &::after {
        bottom: 0;
        height: 315px;
        background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%);
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        overflow: hidden;
        cursor: pointer;
        z-index: 1;
        filter: blur(2px);
        -webkit-filter: blur(2px);
    }

    .open-button {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        top: 50%;
        z-index: 2;
        svg {
            position: relative;
            bottom: 1px;
            path {
                fill: ${theme.colors.white};
            }
        }
        border: 2px solid #e5e7eb;
        width: fit-content;
        height: 50px;
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        vertical-align: middle;
        gap: 10px;
        z-index: 10;
        border-radius: ${theme.borderRadius.normal};
        background: ${theme.colors.deepSeaBlue};
        border-radius: ${theme.borderRadius.huge};
        padding: 25px 35px;
        box-shadow: 0px 0px 20px 0px #00000033;
    }
`

const SideActions = styled.div`
    position: absolute;
    right: 20px;
    bottom: 15px;
    display: flex;
    flex-direction: column;
    gap: 15px;
    z-index: 30;
`

const ActionButton = styled.div<{ isReport?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0px;
    cursor: pointer;
    svg {
        cursor: pointer;
    }

    .count {
        font-size: 13px;
        color: ${theme.colors.black};
        font-weight: 500;
    }
`

const BottomBar = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: ${theme.colors.transparent};
    padding: 5px;
    z-index: 2;
    height: fit-content;
    display: flex;
    align-items: flex-end;
    justify-content: center;
`

const KnowInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    padding: 8px 10px;

    img {
        border: 1px solid #ffffff26;
        cursor: pointer;
    }
    span {
        position: relative;
        top: 3px;
    }

    .knower {
        display: grid;
        align-items: start;
        grid-template-columns: 45px auto;
        gap: 10px;
        width: fit-content;
        p {
            cursor: pointer;
        }
    }

    .title {
        line-height: 20px;
        color: ${theme.colors.white};
        margin-bottom: 4px;
        font-weight: 600;
        z-index: 2;
    }
`

const MetaContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
`

const BadgesContainer = styled.div`
    display: flex;
    gap: 4px;
    flex-wrap: wrap;

    p {
        background: ${theme.colors.backgroundTransparent};
        border-radius: 28px;
        padding: 2px 10px;
        font-size: 12px;
        font-family: var(--font-inter);
        font-weight: 400;
        color: ${theme.colors.white};
        box-shadow: 0px 0px 20px 0px #00000033;
    }
`

interface Props {
    knowSeoInfo: KnowSeoInfo
    isDarkTheme?: boolean
    onContentClick: () => void
    noPadding?: boolean
    imageSource?: string
}

const AppStylePreview = ({ knowSeoInfo, isDarkTheme, onContentClick, noPadding, imageSource }: Props) => {
    const router = useRouter()
    const { t } = _useTranslation('common')
    const parsedLocale = parseLocale(router.locale)
    const { know } = knowSeoInfo

    const handleLike = () => {
        trackEvent(AnalyticsEventKnowConversionLike, { know, referrerScreen: 'tikTokPreview' })
        onContentClick()
    }

    const handleSave = () => {
        trackEvent(AnalyticsEventKnowConversionSave, { know, referrerScreen: 'tikTokPreview' })
        onContentClick()
    }

    const handleShare = () => {
        trackEvent(AnalyticsEventKnowConversionShare, { know, referrerScreen: 'tikTokPreview' })
        onContentClick()
    }

    return (
        <Wrapper noPadding={noPadding}>
            <Container noPadding={noPadding}>
                <ImageContainer onClick={onContentClick}>
                    <NextImage
                        src={imageSource ?? know.thumbnailMediumUrl}
                        alt={knowSeoInfo.title}
                        width={1080}
                        height={1920}
                        priority
                    />
                    <div className="open-button">
                        <ViewIcon />
                        <Body2 fontFamily="var(--font-inter)" fontSize={16} fontWeight={600} color={theme.colors.white}>
                            {t('common/open')}
                        </Body2>
                    </div>
                </ImageContainer>
                <SideActions>
                    <ActionButton onClick={handleLike}>
                        <div className="icon-container">
                            <LikeIcon />
                        </div>
                        <Body2
                            fontFamily="var(--font-inter)"
                            fontSize={12}
                            fontWeight={500}
                            color={theme.colors.white}
                            textAlign="center"
                        >
                            {know.likes}
                        </Body2>
                    </ActionButton>

                    <ActionButton onClick={handleShare}>
                        <div className="icon-container">
                            <CommentIcon />
                            <Body2
                                fontFamily="var(--font-inter)"
                                fontSize={12}
                                fontWeight={500}
                                color={theme.colors.white}
                                textAlign="center"
                            >
                                {know.commentsCount}
                            </Body2>
                        </div>
                    </ActionButton>
                    <ActionButton onClick={handleSave}>
                        <div className="icon-container">
                            <SaveIcon />
                        </div>
                    </ActionButton>

                    <ActionButton onClick={handleShare}>
                        <div className="icon-container">
                            <DownloadIcon />
                        </div>
                    </ActionButton>
                </SideActions>

                <BottomBar>
                    <KnowInfo>
                        <div className="knower">
                            <span onClick={onContentClick}>
                                <ProfilePicture user={know.knower.user} size={45} />
                            </span>
                            <Stack gutter={2}>
                                <Body2
                                    fontFamily="var(--font-open-sans)"
                                    fontSize={14}
                                    fontWeight={500}
                                    color={theme.colors.white}
                                    className="title"
                                    onClick={onContentClick}
                                >
                                    {know.knower.user.name ?? know.knower.user.username}
                                </Body2>
                                <MetaContainer>
                                    <BadgesContainer>
                                        <Body1 onClick={onContentClick}>
                                            {formatDate(know.createdOn, parsedLocale.interfaceLanguageCode)}
                                        </Body1>
                                        {know.subjects?.length > 0 ? (
                                            <Body1 onClick={onContentClick}>{know.subjects?.[0]?.name}</Body1>
                                        ) : null}
                                    </BadgesContainer>
                                </MetaContainer>
                            </Stack>
                        </div>

                        <Body1 className="title" fontFamily="var(--font-open-sans)" fontSize={16} fontWeight={500}>
                            {know.title}
                        </Body1>
                    </KnowInfo>
                </BottomBar>
            </Container>
        </Wrapper>
    )
}

export default AppStylePreview
